import "@/App.css";
import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { SessionAppProvider, useApp } from "@/context/AppContext";
import { AuthProvider, useAuth, mapRoleCodeToLegacyId } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import Layout from "@/components/Layout";
import NfcUiGate from "@/components/NfcUiGate";
import { NFC_UI_VISIBLE } from "@/lib/nfcUiVisibility";
import Login from "@/pages/Login";
import SupportPage from "@/pages/SupportPage";
import PrivacyPage from "@/pages/PrivacyPage";
import Ferie from "@/pages/Ferie";
import FerieDipendente from "@/pages/FerieDipendente";
import FerieMie from "@/pages/FerieMie";
import FerieAnalisi from "@/pages/FerieAnalisi";
import FerieAnalisiApprofondimento from "@/pages/FerieAnalisiApprofondimento";
import FeriePianificazione from "@/pages/FeriePianificazione";
import DipendentiTeam from "@/pages/DipendentiTeam";
import FoundationDashboard from "@/pages/FoundationDashboard";
import AdminUsers from "@/pages/AdminUsers";
import Personale from "@/pages/Personale";
import PersonaScheda from "@/pages/PersonaScheda";
import MieiDocumenti from "@/pages/MieiDocumenti";
import DocumentiPersonale from "@/pages/DocumentiPersonale";
import Turni from "@/pages/Turni";
import ImpostazioniStruttura from "@/pages/ImpostazioniStruttura";
import ImpostazioniOrariTurni from "@/pages/ImpostazioniOrariTurni";
import ImpostazioniFerieFabbisogno from "@/pages/ImpostazioniFerieFabbisogno";
import ImpostazioniFeriePolicy from "@/pages/ImpostazioniFeriePolicy";
import SettingsHome from "@/pages/SettingsHome";
import SetPassword from "@/pages/SetPassword";
import AccessDenied from "@/pages/AccessDenied";
import RequireCap, { RequireGlobalCeo } from "@/components/RequireCap";
import RequireHaccpAccess from "@/features/haccp/RequireHaccpAccess";
import RequireHaccpTemperatureAccess from "@/features/haccp/RequireHaccpTemperatureAccess";
import HaccpDashboard from "@/features/haccp/HaccpDashboard";
import HaccpTemperaturePage from "@/features/haccp/HaccpTemperaturePage";
import PositiveBlastChillingPage from "@/features/haccp/PositiveBlastChillingPage";
import NegativeBlastChillingPage from "@/features/haccp/NegativeBlastChillingPage";
import Audit from "@/pages/Audit";
import NfcPoints from "@/pages/NfcPoints";
import AttendanceRoute from "@/components/AttendanceRoute";
import NfcBrowserAttendance, { NfcAttendanceOutcome } from "@/pages/NfcBrowserAttendance";
import AttendanceQrKiosk from "@/pages/AttendanceQrKiosk";
import AttendanceQrKioskAdmin from "@/pages/AttendanceQrKioskAdmin";
import { HaccpOverview, HaccpSettings } from "@/features/haccp/HaccpPages";
import { getBootstrapView, getPrimaryRoleCode } from "@/lib/authBootstrap";
import { isPureEmployee } from "@/lib/capabilities";

function LegacyRoleBridge() {
  const { me } = useAuth();
  const { currentRoleId, loginConRuolo } = useApp();
  useEffect(() => {
    if (me && !currentRoleId) {
      const legacyId = mapRoleCodeToLegacyId(getPrimaryRoleCode(me));
      if (legacyId) loginConRuolo(legacyId);
    }
  }, [me, currentRoleId, loginConRuolo]);
  return null;
}

function FoundationRoute() {
  const { me } = useAuth();
  if (isPureEmployee(me)) return <FoundationDashboard />;
  return (
    <RequireCap cap="company.view" fallback="/access-denied">
      <FoundationDashboard />
    </RequireCap>
  );
}

function LoadingScreen({ label = 'Caricamento…' }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-app-background" data-testid="app-loading">
      <div className="flex items-center gap-3 text-stone-500 text-sm">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span>{label}</span>
      </div>
    </div>
  );
}

function LegacyFerieAnalysisRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/ferie', search: location.search }} replace />;
}

function BootstrapFailure({ message, retry, signOut, retrying = false }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-app-background px-5" data-testid="bootstrap-error">
      <div className="w-full max-w-md rounded-2xl border border-red-200 bg-white p-7 shadow-sm">
        <h1 className="font-display text-2xl font-bold text-brand-navy">Accesso non completato</h1>
        <p className="mt-3 text-sm leading-relaxed text-stone-600">
          {message || 'Non è stato possibile caricare il profilo autorizzato.'}
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={retry}
            disabled={retrying}
            className="rounded-xl bg-brand-green px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
            data-testid="bootstrap-retry"
          >
            {retrying ? 'Riprovo…' : 'Riprova'}
          </button>
          <button
            type="button"
            onClick={signOut}
            className="rounded-xl border border-stone-200 px-4 py-2.5 text-sm font-semibold text-stone-700"
            data-testid="bootstrap-signout"
          >
            Torna al login
          </button>
        </div>
      </div>
    </div>
  );
}

function ProtectedLayout() {
  const { isAuthenticated, loading, me, error, refreshMe, signOut } = useAuth();
  const { currentRoleId } = useApp();
  const [retrying, setRetrying] = React.useState(false);
  const bootstrapView = getBootstrapView({ loading, isAuthenticated, me, currentRoleId, error });
  if (bootstrapView === 'loading') return <LoadingScreen />;
  if (bootstrapView === 'unauthenticated') return <Navigate to="/" replace />;
  if (bootstrapView === 'error') {
    const retry = async () => {
      setRetrying(true);
      try {
        await refreshMe();
      } finally {
        setRetrying(false);
      }
    };
    const message = error || (
      me
        ? 'Il profilo non contiene un ruolo applicativo valido.'
        : 'Il profilo autorizzato non è stato caricato.'
    );
    return <BootstrapFailure message={message} retry={retry} signOut={signOut} retrying={retrying} />;
  }
  return <Layout />;
}

function App() {
  return (
    <div className="App" data-nfc-ui-visible={NFC_UI_VISIBLE ? 'true' : 'false'}>
      <AuthProvider>
        <SessionAppProvider>
          <LegacyRoleBridge />
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Login />} />
              <Route path="/supporto" element={<SupportPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/imposta-password" element={<SetPassword />} />
              <Route path="/nfc/esito" element={<NfcUiGate><NfcAttendanceOutcome /></NfcUiGate>} />
              <Route path="/nfc/:token" element={<NfcUiGate><NfcBrowserAttendance /></NfcUiGate>} />
              <Route path="/attendance-qr-kiosk" element={<AttendanceQrKiosk />} />
              <Route element={<ProtectedLayout />}>
                <Route path="/foundation" element={
                  <FoundationRoute />
                } />
                <Route path="/audit" element={
                  <RequireCap cap="audit.view"><Audit /></RequireCap>
                } />
                <Route path="/presenze" element={
                  <AttendanceRoute />
                } />
                <Route path="/foundation/users" element={
                  <RequireGlobalCeo><AdminUsers /></RequireGlobalCeo>
                } />
                {/* Admin Ferie console — requires approval-tier capability */}
                <Route path="/ferie" element={
                  <RequireCap anyPrefix={['ferie.approve', 'ferie.balance', 'ferie.config']}
                    fallback="/ferie-mie">
                    <Ferie />
                  </RequireCap>
                } />
                <Route path="/ferie/richieste" element={
                  <RequireCap anyPrefix={['ferie.approve', 'ferie.balance', 'ferie.config']}
                    fallback="/ferie-mie">
                    <Ferie operationalView="richieste" />
                  </RequireCap>
                } />
                <Route path="/ferie/situazione-locale/:localeId" element={
                  <RequireCap cap="ferie.view" fallback="/access-denied">
                    <FerieAnalisiApprofondimento view="locali" />
                  </RequireCap>
                } />
                <Route path="/ferie/situazione-locale" element={
                  <RequireCap cap="ferie.view" fallback="/access-denied">
                    <FerieAnalisiApprofondimento view="locali" />
                  </RequireCap>
                } />
                <Route path="/ferie/situazione-dipendente" element={
                  <RequireCap cap="ferie.view" fallback="/access-denied">
                    <FerieAnalisiApprofondimento view="dipendenti" />
                  </RequireCap>
                } />
                <Route path="/ferie/andamento-mensile" element={
                  <RequireCap cap="ferie.view" fallback="/access-denied">
                    <FerieAnalisiApprofondimento view="mensile" />
                  </RequireCap>
                } />
                <Route path="/ferie/timeline" element={
                  <RequireCap cap="ferie.view" fallback="/access-denied">
                    <FerieAnalisiApprofondimento view="timeline" />
                  </RequireCap>
                } />
                <Route path="/ferie/storico" element={
                  <RequireCap anyPrefix={['ferie.approve', 'ferie.balance', 'ferie.config']}
                    fallback="/ferie-mie">
                    <Ferie operationalView="storico" />
                  </RequireCap>
                } />
                <Route path="/ferie/:id" element={
                  <RequireCap anyPrefix={['ferie.approve', 'ferie.balance', 'ferie.config', 'personale.view']}
                    fallback="/ferie-mie">
                    <FerieDipendente />
                  </RequireCap>
                } />
                <Route path="/ferie/analisi/:legacyId" element={
                  <RequireCap cap="ferie.view" fallback="/access-denied">
                    <FerieAnalisi />
                  </RequireCap>
                } />
                <Route path="/ferie/pianificazione" element={
                  <RequireCap cap="turni.view" fallback="/access-denied">
                    <FeriePianificazione />
                  </RequireCap>
                } />
                <Route path="/ferie/analisi/locali/:locationId" element={
                  <RequireCap cap="ferie.view" fallback="/access-denied">
                    <LegacyFerieAnalysisRedirect />
                  </RequireCap>
                } />
                <Route path="/ferie/analisi/locali" element={
                  <RequireCap cap="ferie.view" fallback="/access-denied">
                    <LegacyFerieAnalysisRedirect />
                  </RequireCap>
                } />
                <Route path="/ferie/analisi/dipendenti" element={
                  <RequireCap cap="ferie.view" fallback="/access-denied">
                    <LegacyFerieAnalysisRedirect />
                  </RequireCap>
                } />
                <Route path="/ferie/analisi/mensile" element={
                  <RequireCap cap="ferie.view" fallback="/access-denied">
                    <LegacyFerieAnalysisRedirect />
                  </RequireCap>
                } />
                <Route path="/ferie/analisi/timeline" element={
                  <RequireCap cap="ferie.view" fallback="/access-denied">
                    <LegacyFerieAnalysisRedirect />
                  </RequireCap>
                } />
                <Route path="/ferie/analisi" element={
                  <RequireCap cap="ferie.view" fallback="/access-denied">
                    <LegacyFerieAnalysisRedirect />
                  </RequireCap>
                } />
                {/* Employee self-service Ferie */}
                <Route path="/ferie-mie" element={
                  <RequireCap anyPrefix={['ferie.view', 'ferie.request']}><FerieMie /></RequireCap>
                } />
                <Route path="/dipendenti" element={
                  <RequireCap cap="personale.view"><DipendentiTeam /></RequireCap>
                } />
                <Route path="/personale" element={
                  <RequireCap cap="personale.view"><Personale /></RequireCap>
                } />
                <Route path="/personale/:id" element={
                  <RequireCap cap="personale.view"><PersonaScheda /></RequireCap>
                } />
                <Route path="/documenti-personale" element={
                  <RequireCap anyPrefix={['employee.view', 'personale.view', 'personale.documents']} fallback="/access-denied">
                    <DocumentiPersonale />
                  </RequireCap>
                } />
                <Route path="/miei-documenti" element={
                  <RequireCap cap="self.view"><MieiDocumenti /></RequireCap>
                } />
                <Route path="/turni" element={
                  <RequireCap anyPrefix={['turni.view', 'availability.self.manage']}><Turni /></RequireCap>
                } />
                <Route path="/haccp" element={
                  <RequireHaccpTemperatureAccess><HaccpDashboard /></RequireHaccpTemperatureAccess>
                } />
                <Route path="/haccp/temperature" element={
                  <RequireHaccpTemperatureAccess><HaccpTemperaturePage /></RequireHaccpTemperatureAccess>
                } />
                <Route path="/haccp/abbattimento-positivo" element={
                  <RequireHaccpTemperatureAccess><PositiveBlastChillingPage /></RequireHaccpTemperatureAccess>
                } />
                <Route path="/haccp/abbattimento-positivo/storico" element={
                  <RequireHaccpTemperatureAccess><PositiveBlastChillingPage /></RequireHaccpTemperatureAccess>
                } />
                <Route path="/haccp/abbattimento-negativo" element={
                  <RequireHaccpTemperatureAccess><NegativeBlastChillingPage /></RequireHaccpTemperatureAccess>
                } />
                <Route path="/haccp/abbattimento-negativo/storico" element={
                  <RequireHaccpTemperatureAccess><NegativeBlastChillingPage /></RequireHaccpTemperatureAccess>
                } />
                <Route path="/impostazioni/haccp" element={
                  <RequireHaccpAccess><HaccpSettings /></RequireHaccpAccess>
                } />
                <Route path="/impostazioni/haccp/stato" element={
                  <RequireHaccpAccess><HaccpOverview /></RequireHaccpAccess>
                } />
                <Route path="/impostazioni" element={
                  <RequireCap anyPrefix={['company', 'location', 'department', 'ferie.config']}><SettingsHome /></RequireCap>
                } />
                <Route path="/impostazioni/struttura-aziendale" element={
                  <RequireCap anyPrefix={['company', 'location', 'department']}><ImpostazioniStruttura /></RequireCap>
                } />
                <Route path="/impostazioni/orari-turni" element={
                  <RequireCap cap="turni.view"><ImpostazioniOrariTurni /></RequireCap>
                } />
                {/* Legacy entry points now converge on the single hierarchy screen. */}
                <Route path="/impostazioni/azienda" element={<Navigate to="/impostazioni/struttura-aziendale" replace />} />
                <Route path="/impostazioni/locali" element={<Navigate to="/impostazioni/struttura-aziendale" replace />} />
                <Route path="/impostazioni/reparti" element={<Navigate to="/impostazioni/struttura-aziendale" replace />} />
                <Route path="/impostazioni/ferie-fabbisogno" element={
                  <RequireCap cap="ferie.config.manage"><ImpostazioniFerieFabbisogno /></RequireCap>
                } />
                <Route path="/impostazioni/ferie-policy" element={
                  <RequireCap anyPrefix={['ferie.view', 'ferie.config']}><ImpostazioniFeriePolicy /></RequireCap>
                } />
                <Route path="/impostazioni/punti-nfc" element={
                  <NfcUiGate><RequireCap cap="attendance.*"><NfcPoints /></RequireCap></NfcUiGate>
                } />
                <Route path="/impostazioni/timbratura-qr" element={
                  <RequireCap cap="attendance.view"><AttendanceQrKioskAdmin /></RequireCap>
                } />
                <Route path="/access-denied" element={<AccessDenied />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-center" richColors />
        </SessionAppProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
